import * as THREE from 'three';
import WebGL from 'three/addons/capabilities/WebGL.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { OrbitControls, RoomEnvironment } from 'three/examples/jsm/Addons.js';

let dracoLoader: DRACOLoader | undefined;
const getDracoLoader = () => {
  if (!dracoLoader) {
    dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
  }
  return dracoLoader;
};

export const init = (selectors: string) => {
  if (!selectors || selectors.trim() === '') return;
  const dom = document.querySelector(selectors) as HTMLDivElement | undefined;
  if (!dom) {
    console.log(`${selectors} not found`);
    return;
  }

  dom.innerHTML = '';

  const scene = new THREE.Scene();

  // @ts-ignore
  // window.scene = scene;
  // @ts-ignore
  // window.THREE = THREE;

  const renderer = new THREE.WebGLRenderer({
    alpha: true,
    antialias: true,
  });
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setSize(dom.clientWidth, dom.clientHeight);

  const camera = new THREE.PerspectiveCamera(
    50,
    dom.clientWidth / dom.clientHeight,
    0.01,
    1000,
  );
  camera.name = 'camera';
  scene.add(camera);

  const pmremGenerator = new THREE.PMREMGenerator(renderer);
  scene.environment = pmremGenerator.fromScene(
    new RoomEnvironment(),
    0.04,
  ).texture;

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enablePan = false;
  controls.update();

  dom.appendChild(renderer.domElement);

  return {
    dom,
    scene,
    camera,
    renderer,
    controls,
  };
};

export const loadJson = async (data: unknown) => {
  const group = new THREE.Group();
  const loader = new THREE.ObjectLoader();
  const object = await loader.parseAsync(data);
  const matrix = new THREE.Matrix4().fromArray((data as any).object.matrix);
  const rotation = new THREE.Euler().setFromRotationMatrix(matrix, 'XYZ');
  group.rotation.set(rotation.x, rotation.y, rotation.z);

  while (object.children.length > 0) {
    group.add(object.children[0]);
  }
  return group;
};

export const updateCamera = async (
  camera: THREE.PerspectiveCamera,
  data: unknown,
) => {
  camera.position.fromArray((data as any).camera.object.matrix.slice(12, 15));
  camera.up.fromArray((data as any).camera.object.up);
  camera.updateProjectionMatrix();
};

export const loadGLTF = async (
  url: string,
  onProgress?: (event: ProgressEvent) => void,
) => {
  const loader = new GLTFLoader();
  loader.setDRACOLoader(getDracoLoader());
  return new Promise<THREE.Group>((resolve, reject) => {
    loader.load(
      url,
      (gltf) => {
        resolve(gltf.scene);
      },
      onProgress,
      reject,
    );
  });
};

export const loadGLTFFromBuffer = async (buffer: ArrayBuffer) => {
  const loader = new GLTFLoader();
  loader.setDRACOLoader(getDracoLoader());
  return new Promise<THREE.Group>((resolve, reject) => {
    loader.parse(buffer, '', (gltf) => resolve(gltf.scene), reject);
  });
};

export const loadFBXFromBuffer = async (buffer: ArrayBuffer) => {
  const { FBXLoader } = await import('three/examples/jsm/loaders/FBXLoader.js');
  const loader = new FBXLoader();
  return loader.parse(buffer, '') as THREE.Group;
};

export const loadFBX = async (
  url: string,
  onProgress?: (event: ProgressEvent) => void,
) => {
  const { FBXLoader } = await import('three/examples/jsm/loaders/FBXLoader.js');
  const loader = new FBXLoader();
  return new Promise<THREE.Group>((resolve, reject) => {
    loader.load(url, resolve, onProgress, reject);
  });
};

export const focusObject = (
  target: THREE.Object3D,
  camera: THREE.PerspectiveCamera,
  margin: number = 1.2,
) => {
  target.updateWorldMatrix(true, true);
  const box = new THREE.Box3().setFromObject(target);
  const boxSize = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxSize = Math.max(boxSize.x, boxSize.y, boxSize.z);
  const fitHeightDistance =
    maxSize / (2 * Math.atan((Math.PI * camera.fov) / 360));
  const fitWidthDistance = fitHeightDistance / camera.aspect;
  const distance = margin * Math.max(fitHeightDistance, fitWidthDistance);
  camera.position.set(center.x, center.y, center.z + distance);
  return center;
};

// from three editor source code
export const focusObject1 = (
  target: THREE.Object3D,
  camera: THREE.PerspectiveCamera,
) => {
  let distance;
  const box = new THREE.Box3();
  const center = new THREE.Vector3();
  const sphere = new THREE.Sphere();
  const delta = new THREE.Vector3();

  box.setFromObject(target);

  if (box.isEmpty() === false) {
    box.getCenter(center);
    distance = box.getBoundingSphere(sphere).radius;
  } else {
    center.setFromMatrixPosition(target.matrixWorld);
    distance = 0.1;
  }

  delta.set(0, 0, 1);
  delta.applyQuaternion(camera.quaternion);
  delta.multiplyScalar(distance * 4);

  // camera.position.copy(center).add(delta);
  camera.position.set(center.x, center.y, center.z + distance);
  return center;
};

export const destoryScene = (scene: THREE.Scene) => {
  scene.traverse((object) => {
    if (object instanceof THREE.Mesh) {
      object.geometry.dispose();
      if (object.material instanceof THREE.Material) {
        object.material.dispose();
      } else if (Array.isArray(object.material)) {
        object.material.forEach((material) => material.dispose());
      }
    }
  });
  while (scene.children.length > 0) {
    scene.remove(scene.children[0]);
  }
};

export const supportWebGL2 = () => {
  return WebGL.isWebGL2Available();
};
